import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState("");
  const [creditDays, setCreditDays] = useState("");
  const [contact, setContact] = useState("");
  const navigate = useNavigate();

  const load = () => {
    api.get("/vendors").then((r) => setVendors(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const addVendor = async (e) => {
    e.preventDefault();
    try {
      await api.post("/vendors", { name, credit_days: creditDays ? parseInt(creditDays, 10) : null, contact: contact || null });
      toast.success("Brand added");
      setShowAdd(false);
      setName("");
      setCreditDays("");
      setContact("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="vendors-page" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Vendor Memory</h1>
          <p className="text-base text-slate-600 dark:text-slate-400">Living per-brand profiles — every event diffed against known context.</p>
        </div>
        <button
          data-testid="add-vendor-btn"
          onClick={() => setShowAdd(!showAdd)}
          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-4 py-2 text-base font-bold text-emerald-950 hover:bg-emerald-400 transition-colors"
        >
          <Plus className="h-4 w-4" /> Add brand
        </button>
      </div>

      {showAdd && (
        <form onSubmit={addVendor} data-testid="add-vendor-form" className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 flex flex-wrap gap-3">
          <input
            data-testid="vendor-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Brand name"
            className="flex-1 min-w-[160px] rounded-md border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-2 text-base text-slate-900 dark:text-slate-100 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
          />
          <input
            data-testid="vendor-credit-days-input"
            value={creditDays}
            onChange={(e) => setCreditDays(e.target.value)}
            type="number"
            placeholder="Credit days"
            className="w-32 rounded-md border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-2 text-base text-slate-900 dark:text-slate-100 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
          />
          <input
            data-testid="vendor-contact-input"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="Contact"
            className="flex-1 min-w-[160px] rounded-md border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-2 text-base text-slate-900 dark:text-slate-100 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
          />
          <button data-testid="vendor-save-btn" type="submit" className="rounded-md bg-emerald-500 px-4 py-2 text-base font-bold text-emerald-950 hover:bg-emerald-400 transition-colors">
            Save
          </button>
        </form>
      )}

      {loading ? (
        <div className="flex h-40 items-center justify-center" data-testid="vendors-loading">
          <div className="h-8 w-8 rounded-full border-2 border-emerald-500 dark:border-emerald-500/60 border-t-transparent animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vendors-grid">
          {vendors.map((v) => (
            <div
              key={v.id}
              data-testid={`vendor-card-${v.id}`}
              onClick={() => navigate(`/vendors/${v.id}`)}
              className="cursor-pointer rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 card-hover hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-200 dark:bg-slate-700 border border-slate-300 dark:border-slate-700">
                  <Building2 className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{v.name}</h3>
                  <p className="text-sm font-mono uppercase tracking-wider text-slate-600 dark:text-slate-400">
                    {v.credit_days ? `${v.credit_days}d credit` : "credit terms n/a"}
                  </p>
                </div>
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-slate-600 dark:text-slate-400">
                {v.current_scheme && (
                  <div className="flex justify-between gap-2">
                    <span className="text-slate-600 dark:text-slate-400">Active scheme</span>
                    <span className="text-emerald-600 dark:text-emerald-400 text-right truncate max-w-[60%]">{v.current_scheme.title}</span>
                  </div>
                )}
                {v.last_price_revision && (
                  <div className="flex justify-between gap-2">
                    <span className="text-slate-600 dark:text-slate-400">Last price revision</span>
                    <span className="text-amber-600 dark:text-amber-400">{new Date(v.last_price_revision.date).toLocaleDateString()}</span>
                  </div>
                )}
                {v.outstanding && (
                  <div className="flex justify-between gap-2">
                    <span className="text-slate-600 dark:text-slate-400">Outstanding</span>
                    <span className="text-rose-600 dark:text-rose-400 font-mono">{v.outstanding}</span>
                  </div>
                )}
              </div>
              <div className="mt-3 flex gap-2 text-sm font-mono uppercase tracking-wider">
                <span className="rounded bg-slate-200 dark:bg-slate-700 px-2 py-0.5 text-slate-600 dark:text-slate-400">{v.open_events || 0} open</span>
                {(v.pending_confirms || 0) > 0 && (
                  <span className="rounded bg-amber-500/15 px-2 py-0.5 text-amber-600 dark:text-amber-400">{v.pending_confirms} to confirm</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Vendors;
